import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Checkbox, Input, Select } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import { formatDateTime } from '../lib/utils';

const LOCALES = ['lv', 'lt', 'et', 'ru', 'en'] as const;

interface Synonym {
  id: string;
  locale: string;
  terms: string[];
  oneWay: boolean;
}

interface SearchReport {
  searches: number;
  zeroResultRate: number;
  zeroResults: { query: string; locale: string; searches: number; lastSearchedAt: string }[];
  top: { query: string; locale: string; searches: number; avgResults: number; clicks: number }[];
}

/** Поиск: синонимы по языкам и что ищут покупатели (в т. ч. без результатов). */
export function SearchPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [locale, setLocale] = useState<string>('lv');
  const [terms, setTerms] = useState('');
  const [oneWay, setOneWay] = useState(false);
  const [days, setDays] = useState(30);
  const [error, setError] = useState<string>();
  const synonyms = useQuery({
    queryKey: ['search', 'synonyms'],
    queryFn: () => api<{ items: Synonym[] }>('/search/synonyms'),
  });
  const report = useQuery({
    queryKey: ['search', 'report', days],
    queryFn: () => api<SearchReport>(`/search/report?days=${String(days)}`),
  });

  const add = async (words: string[], lang = locale) => {
    setError(undefined);
    try {
      await api('/search/synonyms', {
        method: 'POST',
        body: { locale: lang, terms: words, oneWay },
      });
      setTerms('');
      await queryClient.invalidateQueries({ queryKey: ['search', 'synonyms'] });
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const remove = async (id: string) => {
    await api(`/search/synonyms/${id}`, { method: 'DELETE' });
    await queryClient.invalidateQueries({ queryKey: ['search', 'synonyms'] });
  };
  const words = terms
    .split(',')
    .map((w) => w.trim())
    .filter(Boolean);

  return (
    <>
      <PageHeader title={t('srch.title')} description={t('srch.hint')} />
      <div className="grid gap-6">
        <Card>
          <CardHeader title={t('srch.synonyms')} description={t('srch.synonymsHint')} />
          <CardContent className="grid gap-4 text-sm">
            <div className="flex flex-wrap items-end gap-3">
              <Field label={t('srch.language')}>
                {(id) => (
                  <Select
                    id={id}
                    value={locale}
                    onChange={(e) => {
                      setLocale(e.target.value);
                    }}
                  >
                    {LOCALES.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label={t('srch.terms')}>
                {(id) => (
                  <Input
                    id={id}
                    value={terms}
                    placeholder={t('srch.termsPlaceholder')}
                    onChange={(e) => {
                      setTerms(e.target.value);
                    }}
                    className="w-80"
                  />
                )}
              </Field>
              <label className="flex items-center gap-2 pb-2">
                <Checkbox
                  checked={oneWay}
                  onChange={(e) => {
                    setOneWay(e.target.checked);
                  }}
                />
                {t('srch.oneWay')}
              </label>
              <Button disabled={words.length < 2} onClick={() => void add(words)}>
                {t('srch.add')}
              </Button>
            </div>
            {error ? <Alert variant="destructive">{error}</Alert> : null}
            <table className="w-full">
              <tbody>
                {(synonyms.data?.items ?? []).map((s) => (
                  <tr key={s.id} className="border-b last:border-0">
                    <td className="w-12 py-1.5 font-mono text-xs">{s.locale}</td>
                    <td>
                      {s.terms.join(s.oneWay ? ' → ' : ' = ')}{' '}
                      {s.oneWay ? <Badge variant="secondary">{t('srch.oneWay')}</Badge> : null}
                    </td>
                    <td className="text-right">
                      <Button size="sm" variant="outline" onClick={() => void remove(s.id)}>
                        {t('common.remove')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title={t('srch.queries')} description={t('srch.queriesHint')} />
          <CardContent className="grid gap-4 text-sm">
            <div className="flex flex-wrap items-end gap-4">
              <Field label={t('srch.period')}>
                {(id) => (
                  <Select
                    id={id}
                    value={days}
                    onChange={(e) => {
                      setDays(Number(e.target.value));
                    }}
                  >
                    {[7, 30, 90].map((d) => (
                      <option key={d} value={d}>
                        {t('srch.days', { count: d })}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <p>
                {t('srch.searches')}: <strong>{report.data?.searches ?? 0}</strong>
              </p>
              <p>
                {t('srch.zeroRate')}:{' '}
                <strong>{Math.round((report.data?.zeroResultRate ?? 0) * 100)}%</strong>
              </p>
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
              <div>
                <h3 className="mb-2 font-medium">{t('srch.zeroResults')}</h3>
                <table className="w-full">
                  <tbody>
                    {(report.data?.zeroResults ?? []).map((z) => (
                      <tr key={`${z.locale}:${z.query}`} className="border-b last:border-0">
                        <td className="py-1.5">{z.query}</td>
                        <td className="font-mono text-xs">{z.locale}</td>
                        <td className="text-right">{z.searches}</td>
                        <td className="text-right text-xs text-muted-foreground">
                          {formatDateTime(z.lastSearchedAt, i18n.language)}
                        </td>
                        <td className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setLocale(z.locale);
                              setTerms(`${z.query}, `);
                            }}
                          >
                            {t('srch.makeSynonym')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {report.data?.zeroResults.length === 0 ? (
                  <p className="text-muted-foreground">{t('srch.noZero')}</p>
                ) : null}
              </div>
              <div>
                <h3 className="mb-2 font-medium">{t('srch.top')}</h3>
                <table className="w-full">
                  <thead className="text-xs text-muted-foreground">
                    <tr>
                      <th className="text-left font-normal">{t('srch.query')}</th>
                      <th />
                      <th className="text-right font-normal">{t('srch.searches')}</th>
                      <th className="text-right font-normal">{t('srch.results')}</th>
                      <th className="text-right font-normal">{t('srch.clicks')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report.data?.top ?? []).map((q) => (
                      <tr key={`${q.locale}:${q.query}`} className="border-b last:border-0">
                        <td className="py-1.5">{q.query}</td>
                        <td className="font-mono text-xs">{q.locale}</td>
                        <td className="text-right">{q.searches}</td>
                        <td className="text-right">{q.avgResults}</td>
                        <td className="text-right">{q.clicks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
